/**
 * GlassCard Component
 * Glassmorphic card extending shadcn/ui Card
 */

import * as React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether card content is interactive (shows hover effects) */
  interactive?: boolean;
}

/**
 * A glassmorphic card component extending shadcn/ui Card.
 * Applies backdrop blur and semi-transparent background.
 */
const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ className, interactive = false, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        className={cn(
          // Glass morphism styles
          'bg-[var(--glass-bg)]',
          'backdrop-blur-[var(--glass-blur)]',
          '[-webkit-backdrop-filter:blur(var(--glass-blur))]',
          'border-[var(--glass-border)]',
          'shadow-[var(--glass-shadow)]',
          // Override default card background
          'text-[var(--color-charcoal)]',
          // Interactive states
          interactive && [
            'transition-all duration-200',
            'hover:shadow-lg',
            'hover:scale-[1.02]',
            'cursor-pointer',
          ],
          className
        )}
        {...props}
      />
    );
  }
);

GlassCard.displayName = 'GlassCard';

// Re-export Card sub-components with Glass prefix for consistency
const GlassCardHeader = CardHeader;
const GlassCardTitle = CardTitle;
const GlassCardDescription = CardDescription;
const GlassCardContent = CardContent;
const GlassCardFooter = CardFooter;

GlassCardHeader.displayName = 'GlassCardHeader';
GlassCardTitle.displayName = 'GlassCardTitle';
GlassCardDescription.displayName = 'GlassCardDescription';
GlassCardContent.displayName = 'GlassCardContent';
GlassCardFooter.displayName = 'GlassCardFooter';

export {
  GlassCard,
  GlassCardHeader,
  GlassCardTitle,
  GlassCardDescription,
  GlassCardContent,
  GlassCardFooter,
};
